import prepareAssertions from '~/packages/variable/prepareAssertions.util'
import { Variable } from '~/packages/variable/variables.typing'
import { boolean, dateTime, defineAction, enumerated, number, object, string } from '~/prix'
import expression from '~/prix/types/expression'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForIncidence } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import legalEntityAppSebraeAttendanceStatsAction from './legalEntityAppSebraeAttendanceStats.action.json' assert { type: 'json' }
import prefixProperties from '~/prix/utils/prefixProperties'
import cnaeDivision from '~/packages/cnaeDivision/cnaeDivision.entity'
import { pick } from '~/prix/utils/types/object'
import { appSebraeAttendanceVariable } from '../stats/appSebraeAttendanceStats.definition'
import getRecommendedCacheOptions from '~/utils/server/stats/getRecommendedCacheOptions'

const combinedShape = object({
  agentCpf: string().label('CPF do agente'),
  agentPolygonCpf: string().label('Polígono do agente'),
  startDate: dateTime().label('Data de início do atendimento'),

  //legalEntity
  id: string().label('ID', 'Identificador', 'Código'),
  tradeName: string().label('Nome fantasia'),
  corporateName: string().isOptional().label('Razão social'),
  cnpj: string().label('CNPJ'),
  openingDate: dateTime().label('Data de abertura'),
  mainCnaeId: string().label('CNAE principal'),
  mainCnaeDescription: string().label('Descrição do CNAE principal'),
  cnaes: string().label('CNAEs secundários'),
  legalNatureId: string().label('ID da natureza jurídica'),
  legalNatureDescription: string().label('Descrição da natureza jurídica'),
  email: string().label('Email'),
  street: string().label('Logradouro'),
  number: string().label('Número'),
  city: string().label('Cidade'),
  neighborhood: string().label('Bairro'),
  state: string().label('Estado'),
  registrationStatus: enumerated({
    active: 'Ativo',
    inapt: 'Inapto',
    suspended: 'Suspenso',
    extinct: 'Extinto',
  }).label('Status'),
  registrationStatusDate: dateTime().label('Data de situação cadastral'),
  registrationStatusReason: string().label('Motivo do status de registro'),
  size: enumerated({
    MEI: 'MEI',
    ME: 'Micro',
    EPP: 'Pequena',
    other: 'Outros',
    unknown: 'Desconhecido',
  }).label('Porte'),
  ageGroup: enumerated({
    startup: 'Startup',
    young: 'Jovem',
    growth: 'Crescimento',
    established: 'Estabelecida',
  }).label('Maturidade'),
  segmentId: string().label('Identificador do segmento'),
  segmentDescription: string().label('Descrição do segmento'),
  isSimples: boolean().label('É Simples Nacional'),
  isMei: boolean().label('É MEI'),
  capital: number().label('Capital social'),
  phoneNumber1AreaCode: string().label('DDD do telefone principal'),
  fullAddress: string().label('Endereço'),
  fullPhoneNumber1: string().label('Telefone principal'),
  fullPhoneNumber2: string().label('Telefone secundário'),
  pointOnStreet: object({
    type: string().label('Tipo'),
    coordinates: object({
      0: number().label('Longitude'),
      1: number().label('Latitude'),
    }),
  }).label('Ponto na rua'),

  ...prefixProperties('cnaeDivision.', cnaeDivision.schema.shape),
})
export const legalEntityAppSebraeAttendanceExpression = expression(combinedShape.shape)

export const legalEntityAppSebraeAttendanceVariable: Variable = {
  key: 'legalEntityAppSebraeAttendance',
  categoryKey: 'appSebraeAttendance',
  categoryKeys: ['appSebraeAttendance'],
  actionDefinitionKey: legalEntityAppSebraeAttendanceStatsAction.key,
  title: 'Estabelecimentos distintos',
  label: 'Estabelecimentos atendidos por Agentes SEBRAE Na Sua Empresa',
  description:
    'Conta estabelecimentos únicos atendidos e usa o endereço cadastral da Receita Federal do Brasil para localização.',
  unitSingular: 'estabelecimento',
  unitPlural: 'estabelecimentos',
  grammaticalGender: 'male',
  timeType: 'incidence',
  shape: combinedShape,
  betterDensity: 'more',
  relevance: 0.3,
  payload: {
    productName: 'Agentes SEBRAE Na Sua Empresa',
    portfolioName: ['App SEBRAE'],
  },
  basicSelect: ['corporateName', 'startDate'],
  exportSortBy: { key: 'startDate', direction: 'desc' },
  detailedSelect: [
    'id',
    'cnpj',
    'tradeName',
    'corporateName',
    'registrationStatus',
    'registrationStatusDate',
    'openingDate',
    'mainCnaeId',
    'mainCnaeDescription',
    'segmentId',
    'segmentDescription',
    'cnaes',
    'legalNatureId',
    'legalNatureDescription',
    'size',
    'ageGroup',
    'isSimples',
    'isMei',
    'email',
    'street',
    'number',
    'neighborhood',
    'city',
    'state',
    'phoneNumber1AreaCode',
    'fullPhoneNumber1',
  ],
  filterableBy: expression(
    pick(combinedShape.shape, [
      'size',
      'ageGroup',
      'segmentId',
      'registrationStatus',
      'agentCpf',
      'agentPolygonCpf',
    ]),
  ),
  timeSeries: appSebraeAttendanceVariable.timeSeries,
  assertions: prepareAssertions(legalEntityAppSebraeAttendanceExpression),
}

// assertions -> comparar
const legalEntityAppSebraeAttendanceStatsDefinition = defineAction({
  ...legalEntityAppSebraeAttendanceStatsAction,
  input: object({
    variable,
    highlight: legalEntityAppSebraeAttendanceExpression.isOptional(),
    filter: legalEntityAppSebraeAttendanceExpression.isOptional(),
    space: spaceForPoint().isOptional(),
    time: timeForIncidence().isOptional(),
    select: select(combinedShape),
    assertions,
    analysis,
  }),
  output: object({
    items: object({
      value: number(),
      geoId: string().isOptional(),
      geoType: childrenType.isOptional(),
      dateTime: string().isOptional(),
    }),
  }),
  cache: getRecommendedCacheOptions,
})

export default legalEntityAppSebraeAttendanceStatsDefinition
